/**
 * The post-login screens and their nav labels.
 *
 * Plain client-side state (see `AppShell.tsx`) rather than `react-router-dom`: these are flat
 * screens, none of them deep-link-worthy or nested, so a router would add a dependency and an
 * abstraction layer without solving a problem this app actually has.
 *
 * `adminOnly` is a *display* rule and nothing more. Every administrative route is enforced by the
 * server against this deployment's own admin list; hiding a tab is a courtesy to the person, never
 * the thing that protects the endpoint.
 */
export const SCREENS = [
  // First because it is the surface most people signing in are here for; administrators are the
  // minority of users and the ones who know where their tabs are.
  { id: "chat", label: "Chat", adminOnly: false },
  { id: "status", label: "Index status", adminOnly: true },
  // Source-neutral on purpose: the screen itself says "sites" or "shared drives", taking the
  // word from the source's own `scope_noun`. Hidden entirely when scope selection is off.
  { id: "scopes", label: "Indexing scope", adminOnly: true },
  { id: "rules", label: "Rules", adminOnly: true },
  { id: "admins", label: "Admins", adminOnly: true },
  { id: "audit", label: "Audit", adminOnly: true },
  { id: "runs", label: "Runs & sources", adminOnly: true },
] as const;

export type ScreenId = (typeof SCREENS)[number]["id"];

/** Which screens this session cannot see, given who they are and what the deployment enables. */
export function hiddenScreens(options: {
  isAdmin: boolean;
  chatAvailable: boolean;
  scopeSelectionEnabled: boolean;
}): ScreenId[] {
  const hidden = SCREENS.filter((screen) => screen.adminOnly && !options.isAdmin).map(
    (screen) => screen.id as ScreenId,
  );
  if (!options.chatAvailable) {
    hidden.push("chat");
  }
  if (!options.scopeSelectionEnabled) {
    hidden.push("scopes");
  }
  return hidden;
}

/** Where to land on arrival: the chat if this session has it, otherwise the first admin screen. */
export function defaultScreen(chatAvailable: boolean): ScreenId {
  return chatAvailable ? "chat" : "status";
}
